import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { Fragment } from 'react';
import { toast as hotToast, ToastBar, Toaster } from 'react-hot-toast';

import { theme } from '../../../ui/source/theme';

import type { FunctionComponent, ReactElement } from 'react';
import type { Renderable } from 'react-hot-toast';

const ToastCloseIcon: FunctionComponent = () => {
    return (
        <svg fill="none" height="14" viewBox="0 0 14 14" width="14" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M4.52051 4.52051L9.47884 9.47884M9.47884 4.52051L4.52051 9.47884"
                stroke="#B2B2B0"
                strokeLinecap="round"
                strokeWidth="1.5"
            />
        </svg>
    );
};

const ToastCheckIcon: FunctionComponent = () => {
    return (
        <svg fill="none" height="14" viewBox="0 0 14 14" width="14" xmlns="http://www.w3.org/2000/svg">
            <path
                clipRule="evenodd"
                d="M7.00033 12.8337C10.222 12.8337 12.8337 10.222 12.8337 7.00033C12.8337 3.77866 10.222 1.16699 7.00033 1.16699C3.77866 1.16699 1.16699 3.77866 1.16699 7.00033C1.16699 10.222 3.77866 12.8337 7.00033 12.8337ZM6.16872 9.61857L9.57128 5.45989L8.66833 4.72111L6.08211 7.88204L4.95875 6.75868L4.13379 7.58364L6.16872 9.61857Z"
                fill="#018B02"
                fillRule="evenodd"
            />
        </svg>
    );
};

const successStyle = {
    alignItems: 'flex-start',
    background: theme.color.gray[100],
    borderRadius: '0.625rem',
    boxShadow:
        '1px 1px 1px 0px #FBFBFF inset, -0.5px -0.5px 1px 0px rgba(0, 0, 0, 0.07) inset, 0.22px 0.22px 0.31px -0.5px rgba(0, 0, 0, 0.13), 0.3px 0.3px 0.86px -0.75px rgba(0, 0, 0, 0.13), 1.33px 1.33px 1.88px -1.13px rgba(0, 0, 0, 0.11), 2.95px 2.95px 4.18px -1.5px rgba(0, 0, 0, 0.10), 5px 5px 10.6px -1.88px rgba(0, 0, 0, 0.03), -0.32px -0.32px 0px 0px rgba(0, 0, 0, 0.05)',
    color: theme.color.black[900],
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    padding: '0.75rem',
    width: '20rem',
};

const ToastIconContainer = styled.div({
    height: '0.875rem',
    position: 'relative',
    width: '0.875rem',
});

const CloseButton = styled.div({
    cursor: 'pointer',
    height: '0.875rem',
    position: 'relative',
    width: '0.875rem',
});

const ToastHeader = styled.div({
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    justifyContent: 'space-between',
});

const IconWithMessageWrapper = styled.div({
    alignItems: 'center',
    display: 'flex',
    gap: '0.25rem',
});

const ToastMessage = styled.span({
    color: theme.color.black[900],
    ...theme.typography.body.S.medium,
});

const ToastDescription = styled.div({
    alignSelf: 'stretch',
    ...theme.typography.body.S.regular,
    color: theme.color.gray[800],
});

const toastPosition = 'bottom-right';
const toastDuration = 5000;

const createMessage = (title: string, description?: string): string => {
    return description !== undefined && description !== null && description.length > 0
        ? `${title}/${description}`
        : title;
};

const parseMessage = (combinedMessage: string): { readonly description: string; readonly title: string } => {
    const parts = combinedMessage.split('/');
    const title = parts[0] !== undefined ? parts[0].trim() : '';
    const description = parts[1] !== undefined ? parts[1].trim() : '';

    return { description, title };
};

const extractMessageString = (message: Renderable): string | null => {
    if (is.nullOrUndefined(message)) {
        return null;
    }

    const messageString = is.string(message)
        ? message
        : ((message as ReactElement<any>).props.children ?? String(message));

    return messageString;
};

export const FeedbackToaster: FunctionComponent = () => {
    return (
        <Toaster
            containerStyle={{
                bottom: '4rem',
            }}
            position={toastPosition}
            toastOptions={{
                duration: toastDuration,
                style: {
                    ...successStyle,
                },
            }}
        >
            {(toast) => {
                if (toast.id !== 'feedback-toaster') {
                    return <Fragment />;
                }

                return (
                    <ToastBar position={toastPosition} style={successStyle} toast={toast}>
                        {({ message }) => {
                            const messageString = extractMessageString(message);
                            if (messageString === null) {
                                return null;
                            }

                            const { description, title } = parseMessage(messageString);

                            return (
                                <Fragment>
                                    <ToastHeader>
                                        <IconWithMessageWrapper>
                                            <ToastIconContainer>
                                                <ToastCheckIcon />
                                            </ToastIconContainer>
                                            <ToastMessage>{title}</ToastMessage>
                                        </IconWithMessageWrapper>
                                        <CloseButton onClick={() => hotToast.dismiss(toast.id)}>
                                            <ToastCloseIcon />
                                        </CloseButton>
                                    </ToastHeader>
                                    {description.length > 0 && <ToastDescription>{description}</ToastDescription>}
                                </Fragment>
                            );
                        }}
                    </ToastBar>
                );
            }}
        </Toaster>
    );
};

export const showFeedbackSuccessToast = (title: string, description?: string): string => {
    const message = createMessage(title, description);

    return hotToast.success(message, {
        id: 'feedback-toaster',
        duration: toastDuration,
        icon: <ToastCheckIcon />,
        position: toastPosition,
    });
};
